/**
 * The "View as role" banner: always on screen while a preview is
 * active, above the top bar, on every screen of the shell.
 *
 * VIOLET ON PURPOSE. Red and amber belong to the fire overlay, the
 * fire-hold notice and the alert banners; blue is the product accent
 * and reads as "selected". A preview is none of those -- it is a mode
 * the person chose and can leave -- so it gets the one hue nothing
 * else in the shell uses, and is never shaped like an alert (no
 * `role="alert"`, no warning glyph, a calm eye instead).
 *
 * It also says when an action was refused. `assertWriteAllowed` tells
 * every listener; the banner shows a short "not sent" line for a few
 * seconds, so a press anywhere in the console gets a calm answer in a
 * predictable place even when the surface that issued it shows its own
 * error differently.
 */
import { forwardRef, useEffect, useState } from 'react';
import { useRolePreview } from '../auth/useRolePreview';
import { onPreviewRefusal, previewRoleLabel } from '../auth/role-preview';
import './RolePreviewBanner.css';

const REFUSAL_VISIBLE_MS = 6_000;

function EyeGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export const RolePreviewBanner = forwardRef<HTMLDivElement>(function RolePreviewBanner(_props, ref) {
  const { previewRole, exitPreview } = useRolePreview();
  const [refusedAt, setRefusedAt] = useState<number | null>(null);

  useEffect(() => onPreviewRefusal(() => setRefusedAt(Date.now())), []);

  useEffect(() => {
    if (refusedAt === null) return undefined;
    const t = window.setTimeout(() => setRefusedAt(null), REFUSAL_VISIBLE_MS);
    return () => window.clearTimeout(t);
  }, [refusedAt]);

  useEffect(() => {
    if (previewRole === null) setRefusedAt(null);
  }, [previewRole]);

  if (!previewRole) return null;
  const label = previewRoleLabel(previewRole);

  return (
    <div
      className="nx-role-preview"
      ref={ref}
      role="region"
      aria-label="Role preview"
      data-testid="role-preview-banner"
    >
      <span className="nx-role-preview__icon">
        <EyeGlyph />
      </span>
      <span className="nx-role-preview__text">
        Viewing as <strong>{label}</strong>
        <span className="nx-role-preview__sep"> — </span>
        <span className="nx-role-preview__off">actions are off</span>
      </span>
      <span className="nx-role-preview__dot" aria-hidden="true">
        ·
      </span>
      <button
        type="button"
        className="nx-role-preview__exit"
        onClick={exitPreview}
        data-testid="role-preview-exit"
        aria-label={`Exit view as ${label} and return to your own role`}
      >
        Exit
      </button>
      <span
        className="nx-role-preview__refused"
        role="status"
        aria-live="polite"
        data-testid="role-preview-refused"
      >
        {refusedAt !== null ? `Not sent — actions are off while viewing as ${label}.` : ''}
      </span>
    </div>
  );
});
